import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Cpu, BarChart3, Users, Mail, 
  FileText, Film, ChevronRight, Menu, X,
  Phone, MapPin, ArrowRight, Check, Loader,
  Smartphone, Monitor, Zap, Wand2, ArrowLeft,
  MessageSquare, Utensils, Activity, LayoutDashboard,
  Kanban, PenTool, RefreshCw
} from 'lucide-react';
import { playSound, SOUNDS } from '../App';
import confetti from 'canvas-confetti';

// Import preview components
import DashboardPreview from './previews/DashboardPreview';
import KanbanPreview from './previews/KanbanPreview';
import ContentPreview from './previews/ContentPreview';
import FitnessPreview from './previews/FitnessPreview';
import FoodPreview from './previews/FoodPreview';
import ChatPreview from './previews/ChatPreview';

// Mobile Boot Screen - Quick and impactful
const MobileBoot = ({ onComplete }) => {
  const [phase, setPhase] = useState('logo');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Quick logo animation
    const progressInterval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(progressInterval);
          setTimeout(onComplete, 300);
          return 100;
        }
        return p + 4;
      });
    }, 50);

    return () => clearInterval(progressInterval);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(180deg, #008080 0%, #006060 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 9999
      }}
    >
      {/* Windows Flag */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          width: '80px',
          height: '80px',
          gap: '4px',
          marginBottom: '20px'
        }}
      >
        <div style={{ background: '#ff0000', borderRadius: '2px' }} />
        <div style={{ background: '#00ff00', borderRadius: '2px' }} />
        <div style={{ background: '#0000ff', borderRadius: '2px' }} />
        <div style={{ background: '#ffff00', borderRadius: '2px' }} />
      </motion.div>

      {/* Title */}
      <motion.div
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        style={{
          color: 'white',
          fontFamily: "'Segoe UI', sans-serif",
          textAlign: 'center',
          marginBottom: '32px'
        }}
      >
        <div style={{ fontSize: '28px', fontWeight: '300' }}>Autonomous</div>
        <div style={{ fontSize: '42px', fontWeight: 'bold' }}>95</div>
      </motion.div>

      {/* Progress Bar */}
      <div style={{
        width: '200px',
        height: '20px',
        background: '#006060',
        border: '2px solid',
        borderColor: '#004040 #00a0a0 #00a0a0 #004040',
        padding: '3px'
      }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          style={{
            height: '100%',
            background: '#000080'
          }}
        />
      </div>
      <div style={{
        color: 'rgba(255,255,255,0.8)',
        fontSize: '12px',
        marginTop: '12px',
        fontFamily: "'Segoe UI', sans-serif"
      }}>
        Starting Autonomous 95...
      </div>
    </motion.div>
  );
};

// Mobile Navigation Item
const NavItem = ({ icon: Icon, label, active, onClick, highlight }) => (
  <motion.button
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase()}`}
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '3px',
      padding: '6px 8px',
      background: active ? 'rgba(0,0,128,0.2)' : 'transparent',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      flex: 1,
      minWidth: '50px',
      maxWidth: '80px'
    }}
  >
    <div style={{
      width: '28px',
      height: '28px',
      background: highlight 
        ? 'linear-gradient(135deg, rgba(255,215,0,0.3) 0%, rgba(255,107,53,0.2) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(0,0,0,0.1) 100%)',
      border: '2px solid',
      borderColor: highlight
        ? 'rgba(255,215,0,0.7) rgba(180,130,0,0.5) rgba(180,130,0,0.5) rgba(255,215,0,0.7)'
        : 'rgba(255,255,255,0.4) rgba(0,0,0,0.3) rgba(0,0,0,0.3) rgba(255,255,255,0.4)',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: highlight ? '0 0 10px rgba(255,215,0,0.4)' : '1px 1px 2px rgba(0,0,0,0.2)'
    }}>
      <Icon size={16} color={highlight ? '#ffd700' : 'white'} />
    </div>
    <span style={{
      fontSize: '9px',
      color: highlight ? '#ffd700' : 'white',
      fontFamily: "'Segoe UI', sans-serif",
      fontWeight: active ? 'bold' : 'normal',
      textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
    }}>
      {label}
    </span>
  </motion.button>
);

// Mobile Window Header
const MobileWindowHeader = ({ title, onClose }) => (
  <div style={{
    background: 'linear-gradient(90deg, #000080 0%, #1084d0 100%)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 10
  }}>
    <span style={{
      color: 'white',
      fontFamily: "'Segoe UI', sans-serif",
      fontSize: '14px',
      fontWeight: 'bold',
      textShadow: '1px 1px 0 rgba(0,0,0,0.3)'
    }}>
      {title}
    </span>
    <motion.button
      whileTap={{ scale: 0.9 }}
      onClick={onClose}
      style={{
        width: '28px',
        height: '28px',
        background: '#c0c0c0',
        border: '2px solid',
        borderColor: '#fff #404040 #404040 #fff',
        borderRadius: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
      }}
    >
      <X size={14} />
    </motion.button>
  </div>
);

// Home Screen Content
const HomeContent = ({ onNavigate }) => {
  const stats = [
    { value: '6', unit: 'weeks', label: 'to production' },
    { value: '99.7', unit: '%', label: 'uptime SLA' },
    { value: '0', unit: '', label: 'failed pilots' }
  ];

  return (
    <div style={{ padding: '20px', paddingBottom: '100px' }}>
      {/* Hero */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        style={{
          background: 'white',
          border: '2px solid',
          borderColor: '#fff #808080 #808080 #fff',
          padding: '24px 20px',
          marginBottom: '16px'
        }}
      >
        {/* Windows Flag */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          width: '40px',
          height: '40px',
          gap: '2px',
          marginBottom: '16px'
        }}>
          <div style={{ background: '#ff0000', borderRadius: '1px' }} />
          <div style={{ background: '#00ff00', borderRadius: '1px' }} />
          <div style={{ background: '#0000ff', borderRadius: '1px' }} />
          <div style={{ background: '#ffff00', borderRadius: '1px' }} />
        </div>

        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#000080',
          margin: '0 0 12px 0',
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          We build AI that works.
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#333',
          margin: 0,
          lineHeight: 1.5,
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          Not demos. Not prototypes. Production systems that run autonomously and deliver real results.
        </p>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '8px',
          marginBottom: '16px'
        }}
      >
        {stats.map((stat, i) => (
          <div key={i} style={{
            background: 'white',
            border: '2px solid',
            borderColor: '#808080 #fff #fff #808080',
            padding: '16px 8px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#000080',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              {stat.value}
              {stat.unit && <span style={{ fontSize: '12px' }}>{stat.unit}</span>}
            </div>
            <div style={{
              fontSize: '10px',
              color: '#666',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              {stat.label}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <button
          onClick={() => onNavigate('services')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            background: '#c0c0c0',
            border: '2px solid',
            borderColor: '#fff #404040 #404040 #fff',
            cursor: 'pointer',
            fontFamily: "'Segoe UI', sans-serif"
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Cpu size={20} color="#000080" />
            <span style={{ fontWeight: 'bold' }}>Our Services</span>
          </div>
          <ChevronRight size={20} />
        </button>

        <button
          onClick={() => onNavigate('contact')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            background: '#000080',
            color: 'white',
            border: '2px solid',
            borderColor: '#4040ff #000040 #000040 #4040ff',
            cursor: 'pointer',
            fontFamily: "'Segoe UI', sans-serif"
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Mail size={20} />
            <span style={{ fontWeight: 'bold' }}>Contact Us</span>
          </div>
          <ArrowRight size={20} />
        </button>
      </motion.div>

      {/* Location */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        style={{
          marginTop: '24px',
          padding: '16px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        <MapPin size={18} color="white" />
        <span style={{
          color: 'white',
          fontSize: '13px',
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          Oslo, Norway
        </span>
      </motion.div>
    </div>
  );
};

// Services Content
const ServicesContent = () => {
  const services = [
    { title: 'Agentic AI Systems', desc: 'Agents that research, decide, and execute autonomously.' },
    { title: 'AI Strategy', desc: 'Skip the reports. Get a roadmap in production within 90 days.' },
    { title: 'Process Automation', desc: 'Turn manual workflows into intelligent pipelines.' },
    { title: 'Data Intelligence', desc: 'Extract insights that drive decisions, not just dashboards.' }
  ];

  return (
    <div style={{ padding: '20px', paddingBottom: '100px' }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#000080',
        margin: '0 0 20px 0',
        fontFamily: "'Segoe UI', sans-serif"
      }}>
        What We Build
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {services.map((service, i) => (
          <motion.div
            key={i}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: i * 0.1 }}
            style={{
              background: 'white',
              border: '2px solid',
              borderColor: '#fff #808080 #808080 #fff',
              padding: '16px'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <span style={{ color: '#000080', fontWeight: 'bold', fontSize: '14px' }}>►</span>
              <div>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#000080',
                  margin: '0 0 6px 0',
                  fontFamily: "'Segoe UI', sans-serif"
                }}>
                  {service.title}
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: '#333',
                  margin: 0,
                  lineHeight: 1.4,
                  fontFamily: "'Segoe UI', sans-serif"
                }}>
                  {service.desc}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// About Content
const AboutContent = () => {
  const values = [
    'We say what we\'ll do, then do it.',
    'We own the outcome — 99.7% uptime.',
    'We ship fast — weeks, not quarters.',
    'No babysitting required.'
  ];

  return (
    <div style={{ padding: '20px', paddingBottom: '100px' }}>
      {/* Logo and Title */}
      <div style={{
        background: 'white',
        border: '2px solid',
        borderColor: '#fff #808080 #808080 #fff',
        padding: '24px',
        marginBottom: '16px',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          width: '60px',
          height: '60px',
          gap: '3px',
          margin: '0 auto 16px auto'
        }}>
          <div style={{ background: '#ff0000', borderRadius: '2px' }} />
          <div style={{ background: '#00ff00', borderRadius: '2px' }} />
          <div style={{ background: '#0000ff', borderRadius: '2px' }} />
          <div style={{ background: '#ffff00', borderRadius: '2px' }} />
        </div>
        <h2 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#000',
          margin: '0 0 8px 0',
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          Autonomous
        </h2>
        <p style={{
          fontSize: '12px',
          color: '#666',
          margin: 0,
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          Version 95 • Oslo, Norway
        </p>
      </div>

      {/* Values */}
      <div style={{
        background: '#c0c0c0',
        border: '2px solid',
        borderColor: '#fff #808080 #808080 #fff',
        padding: '16px'
      }}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: 'bold',
          color: '#000',
          margin: '0 0 16px 0',
          fontFamily: "'Segoe UI', sans-serif"
        }}>
          Why Autonomous?
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {values.map((value, i) => (
            <motion.div
              key={i}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: i * 0.1 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Check size={16} color="#000080" />
              <span style={{
                fontSize: '13px',
                color: '#000',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                {value}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Contact Form Content
const ContactContent = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // try {
    //   const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    //   const response = await fetch(`${backendUrl}/api/contact`, {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify({
    //       first_name: formData.firstName,
    //       last_name: formData.lastName,
    //       email: formData.email,
    //       company: formData.company,
    //       phone: formData.phone,
    //       project_details: formData.message
    //     })
    //   });
      
    //   if (response.ok) {
    //     setSubmitted(true);
    //     playSound(SOUNDS.notify);
    //   }
    // } catch (error) {
    //   console.error('Error submitting form:', error);
    // }
    
    // setIsSubmitting(false);
    try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${backendUrl}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        project_details: formData.message
      })
    });
    
    // Read response body first, then parse
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse response:', text);
      throw new Error('Serverfeil. Prøv igjen senere.');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Noe gikk galt');
    }
    
    setSubmitted(true);
    playSound(SOUNDS.notify);
  } catch (error) {
    console.error('Error submitting form:', error);
  }
  };

  if (submitted) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{
            background: 'white',
            border: '2px solid',
            borderColor: '#fff #808080 #808080 #fff',
            padding: '32px 24px'
          }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            background: '#000080',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto'
          }}>
            <Check size={32} color="white" />
          </div>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000080',
            margin: '0 0 8px 0',
            fontFamily: "'Segoe UI', sans-serif"
          }}>
            Message Sent!
          </h2>
          <p style={{
            fontSize: '13px',
            color: '#666',
            margin: 0,
            fontFamily: "'Segoe UI', sans-serif"
          }}>
            We'll get back to you within 24 hours.
          </p>
        </motion.div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontFamily: "'Segoe UI', sans-serif",
    background: 'white',
    border: '2px solid',
    borderColor: '#808080 #fff #fff #808080',
    outline: 'none'
  };

  return (
    <div style={{ padding: '20px', paddingBottom: '100px' }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: 'bold',
        color: 'white',
        margin: '0 0 16px 0',
        fontFamily: "'Segoe UI', sans-serif",
        textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
      }}>
        Get In Touch
      </h2>

      <form onSubmit={handleSubmit} style={{
        background: '#c0c0c0',
        border: '2px solid',
        borderColor: '#fff #808080 #808080 #fff',
        padding: '20px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>First Name *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Last Name *</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Message *</label>
            <textarea
              required
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              style={{ ...inputStyle, resize: 'none' }}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 'bold',
              fontFamily: "'Segoe UI', sans-serif",
              background: '#000080',
              color: 'white',
              border: '2px solid',
              borderColor: '#4040ff #000040 #000040 #4040ff',
              cursor: isSubmitting ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isSubmitting ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Sending...
              </>
            ) : (
              <>
                <Mail size={16} />
                Send Message
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// Mobile App Builder Component - WORLD CLASS VERSION
const MobileAppBuilder = ({ onNavigate }) => {
  const [step, setStep] = useState(1);
  const [selectedApp, setSelectedApp] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('ocean');
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  const [buildComplete, setBuildComplete] = useState(false);
  const [buildPhase, setBuildPhase] = useState('');
  const [showPhoneFrame, setShowPhoneFrame] = useState(false);

  const apps = [
    { id: 'dashboard', name: 'AI Analytics', icon: LayoutDashboard, desc: 'Real-time business intelligence', emoji: '📊', color: '#3b82f6', features: ['Live metrics', 'AI insights', 'Custom reports'] },
    { id: 'kanban', name: 'Project Hub', icon: Kanban, desc: 'Agile task management', emoji: '📋', color: '#8b5cf6', features: ['Drag & drop', 'Team collab', 'AI priorities'] },
    { id: 'chat', name: 'AI Assistant', icon: MessageSquare, desc: 'Intelligent conversations', emoji: '🤖', color: '#06b6d4', features: ['Natural language', 'Voice support', '24/7 available'] },
    { id: 'fitness', name: 'Fitness Pro', icon: Activity, desc: 'Health & wellness tracking', emoji: '💪', color: '#10b981', features: ['Step tracking', 'AI coaching', 'Workout plans'] },
    { id: 'food', name: 'Food Express', icon: Utensils, desc: 'Smart food delivery', emoji: '🍔', color: '#f97316', features: ['AI recommendations', 'Live tracking', 'Quick order'] },
    { id: 'content', name: 'Content Studio', icon: PenTool, desc: 'AI-powered content creation', emoji: '✍️', color: '#ec4899', features: ['Text generation', 'Multi-format', 'Brand voice'] },
  ];

  const themes = [
    { id: 'ocean', name: 'Ocean Blue', primary: '#3b82f6', secondary: '#06b6d4', gradient: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)', emoji: '🌊' },
    { id: 'purple', name: 'Royal Purple', primary: '#8b5cf6', secondary: '#d946ef', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)', emoji: '👑' },
    { id: 'emerald', name: 'Emerald', primary: '#10b981', secondary: '#34d399', gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', emoji: '💎' },
    { id: 'sunset', name: 'Sunset', primary: '#f97316', secondary: '#fb923c', gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', emoji: '🌅' },
    { id: 'rose', name: 'Rose Gold', primary: '#ec4899', secondary: '#f472b6', gradient: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', emoji: '🌸' },
    { id: 'midnight', name: 'Midnight', primary: '#6366f1', secondary: '#818cf8', gradient: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', emoji: '🌙' },
  ];

  const buildPhases = [
    { name: 'Analyzing requirements...', icon: '🔍' },
    { name: 'Designing UI/UX...', icon: '🎨' },
    { name: 'Generating components...', icon: '⚙️' },
    { name: 'Integrating AI...', icon: '🤖' },
    { name: 'Running tests...', icon: '✅' },
    { name: 'Deploying app...', icon: '🚀' },
  ];

  const previewComponents = {
    dashboard: DashboardPreview,
    kanban: KanbanPreview,
    content: ContentPreview,
    fitness: FitnessPreview,
    food: FoodPreview,
    chat: ChatPreview,
  };

  const selectedThemeData = themes.find(t => t.id === selectedTheme) || themes[0];
  const customization = {
    companyName: companyName || 'Your Company',
    theme: selectedThemeData
  };

  const handleBuild = () => {
    setIsBuilding(true);
    setBuildProgress(0);
    setBuildPhase(buildPhases[0].name);
    playSound(SOUNDS.click);

    let phaseIndex = 0;
    const phaseInterval = setInterval(() => {
      phaseIndex++;
      if (phaseIndex < buildPhases.length) {
        setBuildPhase(buildPhases[phaseIndex].name);
      }
    }, 700);

    const interval = setInterval(() => {
      setBuildProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          clearInterval(phaseInterval);
          setTimeout(() => {
            setIsBuilding(false);
            setBuildComplete(true);
            setShowPhoneFrame(true);
            playSound(SOUNDS.notify);
            // Epic confetti
            const duration = 3 * 1000;
            const animationEnd = Date.now() + duration;
            const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

            function randomInRange(min, max) {
              return Math.random() * (max - min) + min;
            }

            const confettiInterval = setInterval(function() {
              const timeLeft = animationEnd - Date.now();
              if (timeLeft <= 0) {
                return clearInterval(confettiInterval);
              }
              const particleCount = 50 * (timeLeft / duration);
              confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
              confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
            }, 250);
          }, 500);
          return 100;
        }
        return prev + 1.5;
      });
    }, 40);
  };

  const handleReset = () => {
    setStep(1);
    setSelectedApp(null);
    setCompanyName('');
    setSelectedTheme('ocean');
    setBuildComplete(false);
    setBuildProgress(0);
    setShowPhoneFrame(false);
  };

  const PreviewComponent = selectedApp ? previewComponents[selectedApp.id] : null;

  // Step 1: Select App - WORLD CLASS
  if (step === 1) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
        paddingBottom: '120px'
      }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Premium Header */}
          <div style={{
            padding: '24px 20px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Animated background circles */}
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{ duration: 4, repeat: Infinity }}
              style={{
                position: 'absolute',
                top: '-50px',
                left: '-50px',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)',
                filter: 'blur(60px)'
              }}
            />
            <motion.div
              animate={{ 
                scale: [1.2, 1, 1.2],
                opacity: [0.1, 0.15, 0.1]
              }}
              transition={{ duration: 5, repeat: Infinity }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '-30px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f97316, #ec4899)',
                filter: 'blur(50px)'
              }}
            />

            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', duration: 0.8 }}
              style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)',
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 20px 40px rgba(139, 92, 246, 0.4)',
                position: 'relative'
              }}
            >
              <Wand2 size={36} color="white" />
              <motion.div
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '28px',
                  border: '2px solid rgba(255,255,255,0.3)'
                }}
              />
            </motion.div>

            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'white',
                margin: '0 0 8px 0',
                fontFamily: "'Segoe UI', sans-serif",
                letterSpacing: '-0.5px'
              }}
            >
              App Builder
            </motion.h1>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={{
                fontSize: '15px',
                color: 'rgba(255,255,255,0.7)',
                margin: 0,
                fontFamily: "'Segoe UI', sans-serif"
              }}
            >
              Velg en app og se magien skje ✨
            </motion.p>
          </div>

          {/* App Selection Grid */}
          <div style={{ padding: '0 16px' }}>
            <motion.p
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.5)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: '0 0 12px 4px',
                fontFamily: "'Segoe UI', sans-serif"
              }}
            >
              Velg din app
            </motion.p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {apps.map((app, index) => (
                <motion.button
                  key={app.id}
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 * index + 0.4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedApp(app);
                    playSound(SOUNDS.click);
                    setTimeout(() => setStep(2), 200);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.3s'
                  }}
                >
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    background: `linear-gradient(135deg, ${app.color}40, ${app.color}20)`,
                    border: `1px solid ${app.color}50`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    flexShrink: 0
                  }}>
                    {app.emoji}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: 'white',
                      marginBottom: '4px',
                      fontFamily: "'Segoe UI', sans-serif"
                    }}>
                      {app.name}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.6)',
                      marginBottom: '8px',
                      fontFamily: "'Segoe UI', sans-serif"
                    }}>
                      {app.desc}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {app.features.map((f, i) => (
                        <span key={i} style={{
                          fontSize: '10px',
                          padding: '3px 8px',
                          background: `${app.color}30`,
                          color: app.color,
                          borderRadius: '6px',
                          fontWeight: '500',
                          fontFamily: "'Segoe UI', sans-serif"
                        }}>
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ChevronRight size={20} color="rgba(255,255,255,0.3)" />
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Step 2: Customize - WORLD CLASS
  if (step === 2 && !isBuilding && !buildComplete) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
        paddingBottom: '120px'
      }}>
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header with selected app */}
          <div style={{
            padding: '20px',
            background: `linear-gradient(135deg, ${selectedApp.color}30, ${selectedApp.color}10)`,
            borderBottom: `1px solid ${selectedApp.color}30`
          }}>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setStep(1)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 14px',
                color: 'white',
                fontSize: '13px',
                cursor: 'pointer',
                marginBottom: '16px',
                fontFamily: "'Segoe UI', sans-serif",
                fontWeight: '500'
              }}
            >
              <ArrowLeft size={16} />
              Tilbake
            </motion.button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '18px',
                  background: `linear-gradient(135deg, ${selectedApp.color}, ${selectedApp.color}aa)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  boxShadow: `0 10px 30px ${selectedApp.color}50`
                }}
              >
                {selectedApp.emoji}
              </motion.div>
              <div>
                <h2 style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: 'white',
                  margin: '0 0 4px 0',
                  fontFamily: "'Segoe UI', sans-serif"
                }}>
                  {selectedApp.name}
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.6)',
                  margin: 0,
                  fontFamily: "'Segoe UI', sans-serif"
                }}>
                  {selectedApp.desc}
                </p>
              </div>
            </div>
          </div>

          {/* Customization Form */}
          <div style={{ padding: '24px 20px' }}>
            {/* Company Name */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              style={{ marginBottom: '24px' }}
            >
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '10px',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                <span style={{ fontSize: '18px' }}>🏢</span>
                Firmanavn
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="F.eks. Autonomous, TechCorp..."
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '16px',
                  fontFamily: "'Segoe UI', sans-serif",
                  background: 'rgba(255,255,255,0.08)',
                  border: '2px solid rgba(255,255,255,0.1)',
                  borderRadius: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  color: 'white',
                  transition: 'all 0.3s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = selectedThemeData.primary;
                  e.target.style.background = 'rgba(255,255,255,0.12)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.target.style.background = 'rgba(255,255,255,0.08)';
                }}
              />
            </motion.div>

            {/* Theme Selection */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              style={{ marginBottom: '32px' }}
            >
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '12px',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                <span style={{ fontSize: '18px' }}>🎨</span>
                Fargetema
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '10px' 
              }}>
                {themes.map((theme, i) => (
                  <motion.button
                    key={theme.id}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.05 * i + 0.2 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setSelectedTheme(theme.id);
                      playSound(SOUNDS.click);
                    }}
                    style={{
                      padding: '14px 10px',
                      borderRadius: '14px',
                      background: selectedTheme === theme.id ? theme.gradient : 'rgba(255,255,255,0.05)',
                      border: selectedTheme === theme.id 
                        ? '2px solid white' 
                        : '2px solid rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: selectedTheme === theme.id 
                        ? `0 8px 25px ${theme.primary}60` 
                        : 'none',
                      transition: 'all 0.3s'
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{theme.emoji}</span>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: 'white',
                      fontFamily: "'Segoe UI', sans-serif"
                    }}>
                      {theme.name}
                    </span>
                    {selectedTheme === theme.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                      >
                        <Check size={14} color="white" />
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Preview Card */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              <p style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.5)',
                margin: '0 0 12px 0',
                fontFamily: "'Segoe UI', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Forhåndsvisning
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: selectedThemeData.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  {selectedApp.emoji}
                </div>
                <div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: 'white',
                    fontFamily: "'Segoe UI', sans-serif"
                  }}>
                    {companyName || 'Ditt Firma'} {selectedApp.name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: selectedThemeData.primary,
                    fontFamily: "'Segoe UI', sans-serif"
                  }}>
                    {selectedThemeData.name} tema
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Build Button */}
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBuild}
              style={{
                width: '100%',
                padding: '18px',
                fontSize: '17px',
                fontWeight: '700',
                fontFamily: "'Segoe UI', sans-serif",
                background: selectedThemeData.gradient,
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: `0 10px 30px ${selectedThemeData.primary}50`,
                letterSpacing: '0.5px'
              }}
            >
              <Wand2 size={22} />
              Bygg Min App
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                →
              </motion.span>
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Building Animation - WORLD CLASS
  if (isBuilding) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{ textAlign: 'center', width: '100%', maxWidth: '300px' }}
        >
          {/* Animated Icon */}
          <div style={{ position: 'relative', margin: '0 auto 32px', width: '120px', height: '120px' }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '3px solid transparent',
                borderTopColor: selectedThemeData.primary,
                borderRightColor: selectedThemeData.secondary
              }}
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
              style={{
                position: 'absolute',
                inset: '10px',
                borderRadius: '50%',
                border: '3px solid transparent',
                borderBottomColor: selectedThemeData.primary,
                borderLeftColor: selectedThemeData.secondary
              }}
            />
            <div style={{
              position: 'absolute',
              inset: '20px',
              borderRadius: '50%',
              background: selectedThemeData.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 40px ${selectedThemeData.primary}60`
            }}>
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                style={{ fontSize: '40px' }}
              >
                {selectedApp.emoji}
              </motion.span>
            </div>
          </div>

          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              fontSize: '22px',
              fontWeight: '700',
              color: 'white',
              margin: '0 0 8px 0',
              fontFamily: "'Segoe UI', sans-serif"
            }}
          >
            Bygger {companyName || 'din'} app
          </motion.h2>

          <motion.p
            key={buildPhase}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              fontSize: '15px',
              color: selectedThemeData.primary,
              margin: '0 0 32px 0',
              fontFamily: "'Segoe UI', sans-serif",
              fontWeight: '500'
            }}
          >
            {buildPhase}
          </motion.p>

          {/* Progress Bar */}
          <div style={{
            width: '100%',
            height: '8px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '12px'
          }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${buildProgress}%` }}
              style={{
                height: '100%',
                background: selectedThemeData.gradient,
                borderRadius: '4px',
                boxShadow: `0 0 10px ${selectedThemeData.primary}`
              }}
            />
          </div>

          <p style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.5)',
            margin: 0,
            fontFamily: "'Segoe UI', sans-serif"
          }}>
            {buildProgress}% ferdig
          </p>
        </motion.div>
      </div>
    );
  }

  // Build Complete - Show Preview - WORLD CLASS
  if (buildComplete) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)',
        paddingBottom: '140px'
      }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Success Header */}
          <motion.div
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            style={{
              background: selectedThemeData.gradient,
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '14px',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Check size={28} color="white" strokeWidth={3} />
              </motion.div>
              <div>
                <div style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '700',
                  fontFamily: "'Segoe UI', sans-serif"
                }}>
                  {companyName || 'Din'} {selectedApp.name}
                </div>
                <div style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '12px',
                  fontFamily: "'Segoe UI', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Sparkles size={12} />
                  Ferdig bygget!
                </div>
              </div>
            </div>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleReset}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <RefreshCw size={18} color="white" />
            </motion.button>
          </motion.div>

          {/* Phone Frame Preview */}
          <div style={{
            padding: '24px 20px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.9 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring', delay: 0.3 }}
              style={{
                width: '280px',
                background: '#000',
                borderRadius: '36px',
                padding: '12px',
                boxShadow: `0 30px 60px rgba(0,0,0,0.5), 0 0 40px ${selectedThemeData.primary}30`
              }}
            >
              {/* Notch */}
              <div style={{
                width: '100px',
                height: '24px',
                background: '#000',
                borderRadius: '0 0 16px 16px',
                margin: '0 auto',
                position: 'relative',
                zIndex: 10
              }} />
              {/* Screen */}
              <div style={{
                height: '480px',
                background: '#fff',
                borderRadius: '28px',
                overflow: 'hidden',
                marginTop: '-12px'
              }}>
                {PreviewComponent && <PreviewComponent customization={customization} />}
              </div>
              {/* Home Indicator */}
              <div style={{
                width: '120px',
                height: '5px',
                background: '#333',
                borderRadius: '3px',
                margin: '12px auto 0'
              }} />
            </motion.div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{
              margin: '0 20px',
              padding: '20px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.1)',
              textAlign: 'center'
            }}
          >
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: 'white',
              margin: '0 0 8px 0',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              Imponert? 🤩
            </h3>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.6)',
              margin: '0 0 16px 0',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              La oss bygge dette for deg på ekte!
            </p>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onNavigate('contact')}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '15px',
                fontWeight: '700',
                fontFamily: "'Segoe UI', sans-serif",
                background: selectedThemeData.gradient,
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: `0 10px 30px ${selectedThemeData.primary}40`
              }}
            >
              <Mail size={18} />
              Kontakt Oss
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return null;
};

// Main Mobile App Component
export default function MobileApp() {
  const [bootComplete, setBootComplete] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('home');

  // Check if boot has been shown
  useEffect(() => {
    const hasSeenBoot = sessionStorage.getItem('mobileBoot');
    if (hasSeenBoot) {
      setBootComplete(true);
    }
  }, []);

  const handleBootComplete = () => {
    sessionStorage.setItem('mobileBoot', 'true');
    setBootComplete(true);
    playSound(SOUNDS.startup, 0.3);
  };

  const handleNavigate = (screen) => {
    playSound(SOUNDS.click);
    setCurrentScreen(screen);
  };

  const navItems = [
    { id: 'home', icon: Sparkles, label: 'Home', highlight: true },
    { id: 'builder', icon: Wand2, label: 'Builder', highlight: true },
    { id: 'services', icon: Cpu, label: 'Services' },
    { id: 'contact', icon: Mail, label: 'Contact' }
  ];

  const screenTitles = {
    home: 'Welcome to Autonomous',
    builder: 'app-builder.exe',
    services: 'services.exe',
    about: 'about.txt',
    contact: 'contact.msg'
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#008080',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <AnimatePresence>
        {!bootComplete && (
          <MobileBoot onComplete={handleBootComplete} />
        )}
      </AnimatePresence>

      {bootComplete && (
        <>
          {/* Header - Only show on non-home screens */}
          <AnimatePresence>
            {currentScreen !== 'home' && currentScreen !== 'builder' && (
              <MobileWindowHeader 
                title={screenTitles[currentScreen]}
                onClose={() => handleNavigate('home')}
              />
            )}
          </AnimatePresence>

          {/* Content Area */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <AnimatePresence mode="wait">
              {currentScreen === 'home' && (
                <motion.div
                  key="home"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <HomeContent onNavigate={handleNavigate} />
                </motion.div>
              )}
              {currentScreen === 'services' && (
                <motion.div
                  key="services"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <ServicesContent />
                </motion.div>
              )}
              {currentScreen === 'builder' && (
                <motion.div
                  key="builder"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <MobileAppBuilder onNavigate={handleNavigate} />
                </motion.div>
              )}
              {currentScreen === 'about' && (
                <motion.div
                  key="about"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <AboutContent />
                </motion.div>
              )}
              {currentScreen === 'contact' && (
                <motion.div
                  key="contact"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <ContactContent />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom Navigation - Taskbar Style */}
          <div style={{
            background: '#c0c0c0',
            borderTop: '2px solid white',
            padding: '10px 8px',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
            marginBottom: '40px', /* Space for Emergent badge */
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            zIndex: 9999,
            gap: '4px'
          }}>
            {navItems.map(item => (
              <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={currentScreen === item.id}
                highlight={item.highlight && currentScreen === 'home'}
                onClick={() => handleNavigate(item.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
